import { OUTSCHOOL_TIMEZONE, dayjs } from "@outschool/time";

export enum FundingProgramAbbreviation {
  AZ = "AZ",
  AL = "AL",
  AR = "AR",
  CA = "CA",
  FL = "FL",
  EMA = "EMA",
  IN = "IN",
  ID = "ID",
  KS = "KS",
  MO = "MO",
  NH = "NH",
  NY = "NY",
  NYSCIRS = "NYSCIRS",
  NC = "NC",
  OH = "OH",
  SC = "SC",
  TX = "TX",
  TEFA = "TEFA",
  UT = "UT",
  VA = "VA",
  LA = "LA",
  WY = "WY",
  GA = "GA",
  WV = "WV",
  PRM = "PRM",
  OED = "OED",
  LPSD = "LPSD",
  KP = "KP",
  TTS = "TTS",
}

export const statesHaveFundingProgram = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "FL",
  "GA",
  "HI",
  "ID",
  "IN",
  "IA",
  "LA",
  "MS",
  "MO",
  "MT",
  "NH",
  "NC",
  "OH",
  "OK",
  "OR",
  "SC",
  "TN",
  "UT",
  "WA",
  "WV",
];

// AR is the only ClassWallet program that requires a credit bundle its one-off so lets leave it here for now instead of inside funding_program_versions in DB.
export const programsRequireCreditBundle = ["AR"];

/**
 * @deprecated Use `isFundingProgramAbbreviationAsync()` from `@outschool/funding-programs` (async, DB-backed).
 */
export function isFundingProgramAbbreviation(
  input?: string | null,
): input is FundingProgramAbbreviation {
  if (!input) {
    return false;
  }
  return [
    (input: string) => input,
    (input: string) => input.toLowerCase(),
    (input: string) => input.toUpperCase(),
  ].some(fn =>
    Object.values(FundingProgramAbbreviation).includes(fn(input) as any),
  );
}

export type FundingProgramPropertiesType = {
  abbreviation: FundingProgramAbbreviation;
  isClassWallet: boolean;
  institution?: string;
  programName: string;
  programNameLong: string;
  programNameAbbr: string;
  programFundingSourceUrl?: string;
  programUrl?: string;
  programEndDate?: Date;
  programRemoved?: boolean;
  landingPagePath?: string;
};

/**
 * @deprecated Use `getActiveFundingProgramProperties()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: use `useFundingProgramProperties()` from `@outschool/ui-auth`.
 */
export const FundingProgramProperties: Record<
  FundingProgramAbbreviation,
  FundingProgramPropertiesType
> = {
  [FundingProgramAbbreviation.AZ]: {
    abbreviation: FundingProgramAbbreviation.AZ,
    isClassWallet: true,
    institution: "Arizona Department of Education",
    programNameAbbr: "ESA",
    programName: "Arizona ESA",
    programNameLong: "Empowerment Scholarship Account",
    programUrl: "https://www.azed.gov/esa",
  },
  [FundingProgramAbbreviation.AL]: {
    abbreviation: FundingProgramAbbreviation.AL,
    isClassWallet: true,
    institution: "Alabama Department of Revenue",
    programNameAbbr: "CHOOSE",
    programName: "CHOOSE Act",
    programNameLong:
      "Creating Hope and Opportunity for Our Students' Education ",
  },
  [FundingProgramAbbreviation.AR]: {
    abbreviation: FundingProgramAbbreviation.AR,
    isClassWallet: true,
    institution: "Arkansas Education Freedom Account",
    programNameAbbr: "EFA",
    programName: "Arkansas EFA",
    programNameLong: "Education Freedom Account",
    programUrl:
      "https://dese.ade.arkansas.gov/offices/office-of-school-choice-and-parent-empowerment/education-freedom-accounts",
  },
  [FundingProgramAbbreviation.CA]: {
    abbreviation: FundingProgramAbbreviation.CA,
    isClassWallet: false,
    programNameAbbr: "CA Charter",
    programName: "California Charter",
    programNameLong: "California Charter School Programs",
  },
  [FundingProgramAbbreviation.FL]: {
    abbreviation: FundingProgramAbbreviation.FL,
    isClassWallet: false,
    programNameAbbr: "FES-UA/EO",
    programName: "FL FES-UA/EO",
    programNameLong: "Florida FES-UA/EO",
    programUrl:
      "https://go.stepupforstudents.org/hubfs/GUIDES/FTC%20FES-EO%20Purchasing%20Guide%202023-24.pdf?_gl=1*12pgasb*_ga*NzczNjg5MTAwLjE3MTI2NzAyMTc.*_ga_V4KBYBT1HD*MTcxNTc0NTg1OS42LjEuMTcxNTc0NTg2MS41OC4wLjA.",
    programRemoved: true,
  },
  [FundingProgramAbbreviation.EMA]: {
    abbreviation: FundingProgramAbbreviation.EMA,
    isClassWallet: false,
    programNameAbbr: "Scholarship",
    programName: "Florida Scholarship",
    programFundingSourceUrl: "https://apply.stepupforstudents.org/",
    programNameLong: "Florida Scholarship Programs",
    programUrl:
      "https://support.outschool.com/en/articles/8496249-how-to-purchase-classes-using-your-florida-esa-funds",
  },
  [FundingProgramAbbreviation.ID]: {
    abbreviation: FundingProgramAbbreviation.ID,
    isClassWallet: true,
    institution: "Idaho Office of the State Board of Education",
    programNameAbbr: "EPP",
    programName: "Idaho EPP",
    programNameLong: "Empowering Parents Program",
    programUrl: "https://empoweringparents.idaho.gov/",
  },
  [FundingProgramAbbreviation.IN]: {
    abbreviation: FundingProgramAbbreviation.IN,
    isClassWallet: true,
    institution: "Indiana Office of the State Treasurer",
    programNameAbbr: "INESA",
    programName: "Indiana ESA",
    programNameLong: "Education Scholarship Account",
    programUrl: "https://www.in.gov/tos/inesa/",
  },
  [FundingProgramAbbreviation.KS]: {
    abbreviation: FundingProgramAbbreviation.KS,
    isClassWallet: false,
    programNameAbbr: "KEEP",
    programName: "KEEP",
    programNameLong: "Kansas Education Enrichment Program",
    programUrl: "https://www.keep.ks.gov/how-it-works",
    programRemoved: true,
  },
  [FundingProgramAbbreviation.NH]: {
    abbreviation: FundingProgramAbbreviation.NH,
    isClassWallet: true,
    institution: "Children's Scholarship Fund NH",
    programNameAbbr: "EFA",
    programName: "New Hampshire EFA",
    programNameLong: "Education Freedom Accounts (EFA)",
    programUrl:
      "https://nh.scholarshipfund.org/apply/nh-education-freedom-accounts/",
  },
  [FundingProgramAbbreviation.NY]: {
    abbreviation: FundingProgramAbbreviation.NY,
    isClassWallet: true,
    institution: "Children's Scholarship Fund NY",
    programNameAbbr: "CSF",
    programName: "New York CSF",
    programNameLong: "Children's Scholarship Fund of NY",
    programUrl: "https://scholarshipfund.org/new-york/",
  },
  [FundingProgramAbbreviation.NYSCIRS]: {
    abbreviation: FundingProgramAbbreviation.NYSCIRS,
    isClassWallet: true,
    institution: "NYSCIRS",
    programNameAbbr: "NYSCIRS",
    programName: "New York State CIRS",
    programNameLong:
      "New York State Coalition for Independent and Religious Schools",
  },
  [FundingProgramAbbreviation.NC]: {
    abbreviation: FundingProgramAbbreviation.NC,
    isClassWallet: true,
    institution: "North Carolina State Education Assistance Authority",
    programNameAbbr: "ESA+",
    programName: "North Carolina ESA+",
    programNameLong: "Education Student Accounts (ESA+) program",
    programUrl: "https://k12.ncseaa.edu/the-education-student-accounts/",
  },
  [FundingProgramAbbreviation.OH]: {
    abbreviation: FundingProgramAbbreviation.OH,
    isClassWallet: false,
    programNameAbbr: "OH ACE",
    programName: "OH ACE",
    programNameLong: "Ohio ACE",
    programRemoved: true,
  },
  [FundingProgramAbbreviation.SC]: {
    abbreviation: FundingProgramAbbreviation.SC,
    isClassWallet: true,
    institution: "SC - Education Scholarship Trust Fund",
    programNameAbbr: "ESTF",
    programName: "South Carolina ESTF",
    programNameLong: "Education Scholarship Trust Fund",
    programUrl: "https://www.classwallet.com/programs/southcarolinaestf/",
  },
  [FundingProgramAbbreviation.TX]: {
    abbreviation: FundingProgramAbbreviation.TX,
    isClassWallet: true,
    institution: "Texas Education Agency",
    programNameAbbr: "PDSES",
    programName: "Texas PDSES",
    programNameLong: "Parent-Directed Special Education Services (PDSES)",
    programUrl:
      "https://tea.texas.gov/academics/special-student-populations/special-education/parent-directed-special-education-services-pdses",
  },
  [FundingProgramAbbreviation.TEFA]: {
    abbreviation: FundingProgramAbbreviation.TEFA,
    isClassWallet: false,
    programNameAbbr: "TEFA",
    programName: "Texas EFA",
    programNameLong: "Texas Education Freedom Accounts",
    programUrl: "https://educationfreedom.texas.gov/",
    landingPagePath: "/lp/texas-tefa",
  },
  [FundingProgramAbbreviation.UT]: {
    abbreviation: FundingProgramAbbreviation.UT,
    isClassWallet: false,
    programNameAbbr: "UFA",
    programName: "Utah FA",
    programNameLong: "Fits All Scholarship Program (UFA)",
    programUrl:
      "https://ufascholarship.com/wp-content/uploads/2024/02/UFA_FamilyHandbook-4-1.pdf",
    landingPagePath: "/online-classes/utah-esa",
  },
  [FundingProgramAbbreviation.MO]: {
    abbreviation: FundingProgramAbbreviation.MO,
    isClassWallet: true,
    // Currently waiting on Institution Name
    programNameAbbr: "MOScholars",
    programName: "MOScholars",
    programNameLong: "Scholars K-12 Scholarship Program",
    programUrl: "https://treasurer.mo.gov/MOScholars/",
  },
  [FundingProgramAbbreviation.LA]: {
    abbreviation: FundingProgramAbbreviation.LA,
    isClassWallet: false,
    programNameAbbr: "GATOR",
    programName: "Louisiana GATOR",
    programNameLong: "Giving All True Opportunity to Rise (GATOR)",
    programUrl:
      "https://doe.louisiana.gov/topic-pages/louisiana-school-choice/la-gator",
    landingPagePath: "/online-classes/louisiana-gator",
  },
  [FundingProgramAbbreviation.WY]: {
    abbreviation: FundingProgramAbbreviation.WY,
    isClassWallet: false,
    programNameAbbr: "ESA",
    programName: "Wyoming ESA",
    programNameLong: "Education Savings Account",
    programUrl: "https://edu.wyoming.gov/parents/education-savings-accounts",
  },
  [FundingProgramAbbreviation.GA]: {
    abbreviation: FundingProgramAbbreviation.GA,
    isClassWallet: false,
    programNameAbbr: "GPS",
    programName: "Georgia Promise",
    programNameLong: "Promise Scholarship",
    programUrl: "https://mygeorgiapromise.org",
    landingPagePath: "/online-classes/georgia-promise",
  },
  [FundingProgramAbbreviation.WV]: {
    abbreviation: FundingProgramAbbreviation.WV,
    isClassWallet: false,
    programNameAbbr: "Hope",
    programName: "West Virginia Hope",
    programNameLong: "Hope Scholarship",
    programUrl: "https://hopescholarshipwv.gov/",
  },
  [FundingProgramAbbreviation.PRM]: {
    abbreviation: FundingProgramAbbreviation.PRM,
    isClassWallet: false,
    programNameAbbr: "Prisma",
    programName: "Prisma",
    programNameLong: "Prisma",
    programUrl: "https://opened.co/login",
  },
  [FundingProgramAbbreviation.OED]: {
    abbreviation: FundingProgramAbbreviation.OED,
    isClassWallet: false,
    programNameAbbr: "OpenEd",
    programName: "OpenEd",
    programNameLong: "OpenEd",
    programUrl: "https://opened.co/login",
  },
  [FundingProgramAbbreviation.VA]: {
    abbreviation: FundingProgramAbbreviation.VA,
    isClassWallet: true,
    // Program ended, not adding an institution
    programNameAbbr: "VA LAG",
    programName: "VA LAG",
    programNameLong: "Virginia LAG",
    programUrl:
      "https://www.doe.virginia.gov/parents-students/for-parents/k-12-learning-acceleration-grants",
    programEndDate: dayjs("2024-05-31").toDate(),
    programRemoved: true,
  },
  [FundingProgramAbbreviation.LPSD]: {
    abbreviation: FundingProgramAbbreviation.LPSD,
    isClassWallet: false,
    programNameAbbr: "LPSD",
    programName: "Lake and Peninsula School District",
    programNameLong: "Lake and Peninsula School District",
    programUrl: "https://www.lpsd.com",
  },
  [FundingProgramAbbreviation.KP]: {
    abbreviation: FundingProgramAbbreviation.KP,
    isClassWallet: false,
    programNameAbbr: "KP",
    programName: "KaiPod Learning",
    programNameLong: "KaiPod Learning",
    programUrl: "https://kaipodlearning.com",
  },
  [FundingProgramAbbreviation.TTS]: {
    abbreviation: FundingProgramAbbreviation.TTS,
    isClassWallet: false,
    programNameAbbr: "TTS",
    programName: "The Talent School",
    programNameLong: "The Talent School",
  },
};

/**
 * @deprecated Use `getActiveFundingProgramProperties()` from `@outschool/funding-programs` and filter by `isClassWallet`.
 * Frontend: use `useFundingProgramProperties().classWalletAbbreviations` from `@outschool/ui-auth`.
 */
export const ClassWalletFundingProgramAbbreviations = Object.values(
  FundingProgramAbbreviation,
).filter(abbr => FundingProgramProperties[abbr].isClassWallet);

/**
 * @deprecated Use `getFundingProgramProperties()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: use `useFundingProgramProperties().findByAbbreviation()` from `@outschool/ui-auth`.
 */
export function getFundingProgramProperties(
  inputFundingProgramAbbr?: string | null,
) {
  if (!inputFundingProgramAbbr) {
    return null;
  }

  const abbr = Object.values(FundingProgramAbbreviation).find(
    abbr => abbr === inputFundingProgramAbbr.toUpperCase(),
  );

  if (!abbr) {
    return null;
  }

  return FundingProgramProperties[abbr];
}

const normalizeInstitutionName = (s: string) =>
  s.trim().toLowerCase().normalize();
/**
 * @deprecated Use `getFundingProgramPropertiesByInstitution()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: use `useFundingProgramProperties().findByInstitution()` from `@outschool/ui-auth`.
 */
export function getFundingProgramPropertiesByInstition(
  institution?: string | null,
) {
  if (!institution) {
    return null;
  }

  const properties = Object.values(FundingProgramProperties).find(
    prop =>
      !!prop.institution &&
      normalizeInstitutionName(prop.institution) ===
        normalizeInstitutionName(institution),
  );

  return properties;
}

/**
 * @deprecated Use `getFundingProgramNameToAbbr()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: derive from `useFundingProgramProperties().programs` in `@outschool/ui-auth`.
 */
export function getFundingProgramNameToAbbr(): Map<
  string,
  FundingProgramAbbreviation
> {
  const map = new Map<string, FundingProgramAbbreviation>();
  Object.entries(FundingProgramProperties).forEach(([abbr, properties]) => {
    if (!properties.isClassWallet && !properties.programRemoved) {
      map.set(properties.programName, abbr as FundingProgramAbbreviation);
    }
  });
  return map;
}

/**
 * @deprecated Use `getFundingProgramWaitPeriod()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: use `useFundingProgramProperties().getWaitPeriodDays()` from `@outschool/ui-auth`.
 */
export function getFundingProgramWaitPeriod(
  fundingProgram: string | undefined,
): number {
  switch (fundingProgram) {
    case FundingProgramAbbreviation.TX:
    case FundingProgramAbbreviation.IN:
    case FundingProgramAbbreviation.NH:
    case FundingProgramAbbreviation.VA:
    case FundingProgramAbbreviation.NY:
    case FundingProgramAbbreviation.SC:
      return 2;
    case FundingProgramAbbreviation.AL:
      return 3;
    case FundingProgramAbbreviation.NC:
    case FundingProgramAbbreviation.MO:
      return 5;
    case FundingProgramAbbreviation.ID:
    case FundingProgramAbbreviation.NYSCIRS:
      return 7;
    case FundingProgramAbbreviation.KS:
      return 42;
    default:
      // Currently AR, AZ
      return 0;
  }
}

export const esaUtmCampaigns = ["Odyssey", "SUFS", "SFT"];

/**
 * @deprecated Use `getFundingProgramFromLandingPagePath()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: use `useFundingProgramProperties().findByLandingPagePath()` from `@outschool/ui-auth`.
 */
export function getFundingProgramFromLandingPagePath(
  landingPagePath: string,
): FundingProgramPropertiesType | null {
  return (
    Object.values(FundingProgramProperties).find(
      prop =>
        prop.landingPagePath && landingPagePath.includes(prop.landingPagePath),
    ) || null
  );
}

/**
 * @deprecated Use `getFundingProgramEndDate()` from `@outschool/funding-programs` (async, DB-backed).
 * Frontend: use `useFundingProgramProperties().getProgramEndDate()` from `@outschool/ui-auth`.
 */
export function getFundingProgramEndDate(
  fundingProgram: string | undefined,
  ignoreFirstDeadline = false,
): Date | null {
  switch (fundingProgram) {
    case FundingProgramAbbreviation.VA:
      const deadlines = [
        new Date("2023-12-30T23:59:00"),
        new Date("2024-05-30T23:59:00"),
      ];
      return getNextValidFundingProgramDeadline(deadlines, ignoreFirstDeadline);
    default:
      return null;
  }
}

function getNextValidFundingProgramDeadline(
  deadlines: Date[],
  ignoreFirstDeadline: boolean,
): Date | null {
  if (!deadlines || deadlines.length < 1) {
    return null;
  }

  const today = dayjs();
  let firstDeadlineIgnored = false;

  for (const date of deadlines) {
    if (
      dayjs
        .tz(today, OUTSCHOOL_TIMEZONE)
        .isAfter(dayjs.tz(dayjs(date), OUTSCHOOL_TIMEZONE))
    ) {
      continue;
    } else {
      if (ignoreFirstDeadline && !firstDeadlineIgnored) {
        firstDeadlineIgnored = true;
        continue;
      } else {
        return date;
      }
    }
  }
  return null;
}
